import React, { useMemo, useState } from 'react';
import { Star, ShieldCheck, Check, EnvelopeSimple, X, SealCheck, Warning } from '@phosphor-icons/react';
import { Logo } from '../components/layout/Logo';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Spinner } from '../components/ui/Spinner';
import { cn, getSession, clearSession, saveStoredReview, StoredReview } from '../lib/utils';
import { PAGE_ROUTE } from '../lib/routes';
type SubjectType = 'tenant' | 'landlord' | 'agent';
type AccessMode = 'logged-in-match' | 'blocked' | 'anon';
interface ReviewSubmissionProps {
  navigate: (page: string, context?: any) => void;
  context?: {
    token?: string;
    subjectType?: SubjectType;
    subjectName?: string;
    subjectSlug?: string;
    targetSlug?: string;
  };
}
type Step = 'ratings' | 'details' | 'otp' | 'success';
interface RatingCategory {
  key: string;
  label: string;
}
/**
 * Category sets per subject. Each subject is evaluated on the dimensions that
 * matter for THEIR role in the rental — a tenant gets cleanliness/payments,
 * a landlord gets responsiveness/fairness, an agent gets professionalism etc.
 */
const CATEGORIES_BY_SUBJECT: Record<SubjectType, RatingCategory[]> = {
  tenant: [{
    key: 'cleanliness',
    label: 'Curățenie și întreținere'
  }, {
    key: 'communication',
    label: 'Comunicare'
  }, {
    key: 'payments',
    label: 'Plata la timp'
  }, {
    key: 'rules',
    label: 'Respectarea regulilor'
  }],
  landlord: [{
    key: 'communication',
    label: 'Comunicare pe parcursul închirierii'
  }, {
    key: 'responsiveness',
    label: 'Promptitudine la sesizări'
  }, {
    key: 'fairness',
    label: 'Corectitudine (garanție, costuri)'
  }, {
    key: 'accuracy',
    label: 'Acuratețea descrierii proprietății'
  }],
  agent: [{
    key: 'communication',
    label: 'Comunicare pe parcursul căutării'
  }, {
    key: 'professionalism',
    label: 'Profesionalism'
  }, {
    key: 'knowledge',
    label: 'Cunoștințe despre proprietate'
  }, {
    key: 'availability',
    label: 'Disponibilitate la vizionări'
  }]
};
// Gender-neutral title pattern — modelled on Trustpilot / Google Reviews
// ("Write a review for X"). Avoids Romanian gender agreement issues with
// pronouns/articles (e.g. "Evaluează-l pe" vs "Evaluează-o pe").
const TITLE_BY_SUBJECT: Record<SubjectType, (name: string) => string> = {
  tenant: name => `Recenzie pentru ${name}`,
  landlord: name => `Recenzie pentru ${name}`,
  agent: name => `Recenzie pentru ${name}`
};
const SUBTITLE_BY_SUBJECT: Record<SubjectType, string> = {
  tenant: 'Recenzia ta îi va ajuta pe alți proprietari să aibă încredere când primesc aplicări.',
  landlord: 'Recenzia ta îi va ajuta pe alți chiriași să aibă încredere când caută o locuință.',
  agent: 'Recenzia ta îi va ajuta pe alți chiriași să aibă încredere când lucrează cu acest agent.'
};
const REVIEW_PROMPT_BY_SUBJECT: Record<SubjectType, (name: string) => string> = {
  tenant: name => `Cum a fost experiența cu ${name}?`,
  landlord: name => `Cum a fost experiența de închiriere cu ${name}?`,
  agent: name => `Cum a fost procesul de închiriere cu ${name}?`
};
const DETAILS_HINT_BY_SUBJECT: Record<SubjectType, string> = {
  tenant: 'Detaliile ajută alți proprietari să ia o decizie.',
  landlord: 'Detaliile ajută alți chiriași să ia o decizie informată.',
  agent: 'Detaliile ajută alți chiriași să aleagă agentul potrivit.'
};
/**
 * Who should write the review for a given subject? Landlord/agent reviews are
 * written by tenants (demand). Tenant reviews are written by landlords/agents
 * (supply).
 */
function expectedReviewerRole(subjectType: SubjectType): 'demand' | 'supply' {
  return subjectType === 'tenant' ? 'supply' : 'demand';
}
export function ReviewSubmission({
  navigate,
  context
}: ReviewSubmissionProps) {
  const subjectType: SubjectType = context?.subjectType || 'tenant';
  const subjectName = context?.subjectName || 'Andrei Popescu';
  const subjectSlug = context?.subjectSlug;
  const targetSlug = context?.targetSlug || '';
  const categories = CATEGORIES_BY_SUBJECT[subjectType];
  const session = useMemo(() => getSession(), []);
  // Decide access mode:
  //  • logged-in-match — session matches the expected reviewer role AND, when
  //    a targetSlug was specified, the session's slug equals it. Pre-fill
  //    identity, skip OTP.
  //  • blocked — same role but wrong identity (strict token check).
  //  • anon — no session, role mismatch, or no slug available → current
  //    anonymous flow (name/email/OTP).
  const accessMode: AccessMode = useMemo(() => {
    if (!session) return 'anon';
    const expectedRole = expectedReviewerRole(subjectType);
    if (session.role !== expectedRole) return 'anon';
    if (targetSlug && session.slug && session.slug !== targetSlug) {
      return 'blocked';
    }
    return 'logged-in-match';
  }, [session, subjectType, targetSlug]);
  const [step, setStep] = useState<Step>('ratings');
  const [ratings, setRatings] = useState<Record<string, number>>(() => Object.fromEntries(categories.map(c => [c.key, 0])));
  const [body, setBody] = useState('');
  // First/last name captured separately so we can display the reviewer as
  // "Firstname L." on the public profile (last name abbreviated to initial)
  // while still matching the full name against an official document during
  // moderation.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleRating = (categoryKey: string, value: number) => {
    setRatings(prev => ({
      ...prev,
      [categoryKey]: value
    }));
  };
  const allRated = categories.every(c => (ratings[c.key] || 0) > 0);
  const isLoggedInReviewer = accessMode === 'logged-in-match';
  // For logged-in reviewers, only the body needs to validate (≥20 chars).
  const detailsValid = isLoggedInReviewer ? body.length >= 20 : body.length >= 20 && firstName.trim().length > 0 && lastName.trim().length > 0 && email.includes('@');
  const handleSendOtp = async () => {
    if (!detailsValid) return;
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 800));
    setIsSubmitting(false);
    setStep('otp');
  };
  /**
   * Build a StoredReview from current form state and persist to localStorage.
   * Idempotent within a single session — same id generated only once because
   * we cache it in a ref. Called right before transitioning to 'success'.
   */
  const persistReview = () => {
    if (!subjectSlug) return; // nothing to attach the review to
    const categoryRatings = {
      ...ratings
    };
    const values = Object.values(categoryRatings).filter(v => v > 0);
    const avg = values.length > 0 ? Math.round(values.reduce((a, b) => a + b, 0) / values.length * 10) / 10 : 0;
    const now = new Date();
    const dateLabel = now.toLocaleDateString('ro-RO', {
      month: 'long',
      year: 'numeric'
    });
    const reviewerRoleLabel = session?.role === 'supply' ? session.subRole === 'agent' ? 'Agent' : 'Proprietar' : 'Chiriaș';
    const review: StoredReview = {
      id: 'rev-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
      subjectType,
      subjectSlug,
      reviewerSlug: session?.slug,
      reviewerName: isLoggedInReviewer ? session?.name || '' :
      // Display name on public profile abbreviates the last name to an
      // initial (e.g. "Radu M.") — full name is kept for document match
      // during moderation but never shown publicly.
      `${firstName.trim()} ${lastName.trim().charAt(0).toUpperCase()}.`,
      reviewerEmail: isLoggedInReviewer ? session?.email : email.trim(),
      reviewerAvatar: session?.avatar,
      reviewerRole: reviewerRoleLabel,
      rating: avg,
      categoryRatings,
      body: body.trim(),
      date: dateLabel,
      createdAt: now.toISOString(),
      // Logged-in reviewers are mutually-verified through the platform → transactional.
      // Anonymous reviewers pass OTP verification but lack a paper trail → pending until
      // a future iteration adds contract upload (verified_by_document).
      status: isLoggedInReviewer ? 'transactional' : 'pending',
      reviewerVerified: isLoggedInReviewer ? true : false
    };
    saveStoredReview(review);
  };
  // Logged-in reviewers skip OTP — submit straight to success.
  const handlePublishLoggedIn = async () => {
    if (!detailsValid) return;
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 800));
    persistReview();
    setIsSubmitting(false);
    setStep('success');
  };
  const handleVerifyOtp = async () => {
    if (otp.length !== 6) return;
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 1200));
    persistReview();
    setIsSubmitting(false);
    setStep('success');
  };
  const goHome = () => navigate(PAGE_ROUTE.LANDING_PAGE);
  const handleSwitchAccount = () => {
    clearSession();
    navigate(PAGE_ROUTE.LOGIN);
  };
  // ── Strict block ─────────────────────────────────────────────────────────
  if (accessMode === 'blocked') {
    return <div className="min-h-screen bg-dark-1 flex flex-col" data-id="element-3952">
        <header className="h-16 border-b border-dark-3 flex items-center justify-between px-4 sm:px-6 shrink-0" data-id="element-3953">
          <button type="button" onClick={goHome} className="flex items-center cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-lime-1 rounded-sm" aria-label="Acasă" data-id="element-3954">
            <Logo data-id="element-3955" />
          </button>
          <button type="button" onClick={goHome} className="w-9 h-9 inline-flex items-center justify-center rounded-md text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer" aria-label="Închide" data-id="element-3956">
            <X size={18} weight="bold" data-id="element-3957" />
          </button>
        </header>
        <main className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8" data-id="element-3958">
          <div className="w-full max-w-md bg-dark-2 border border-dark-3 rounded-2xl p-6 sm:p-8 text-center space-y-5" data-id="element-3959">
            <div className="w-14 h-14 rounded-full bg-dark-3 border border-dark-4 flex items-center justify-center mx-auto" data-id="element-3960">
              <Warning size={26} weight="regular" className="text-light-2" data-id="element-3961" />
            </div>
            <div className="space-y-2" data-id="element-3962">
              <h1 className="text-lg font-semibold text-light-1" data-id="element-3963">
                Acest link a fost trimis altcuiva
              </h1>
              <p className="text-sm text-light-3 leading-relaxed" data-id="element-3964">
                Ești logat ca <strong data-id="element-3965">{session?.name}</strong>, dar acest link
                de recenzie a fost trimis altui utilizator. Pentru a continua,
                loghează-te cu contul corect sau întoarce-te acasă.
              </p>
            </div>
            <div className="flex flex-col gap-2 pt-2" data-id="element-3966">
              <button type="button" onClick={handleSwitchAccount} className="w-full h-11 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 text-sm font-semibold transition-colors cursor-pointer" data-id="element-3967">
                Schimbă contul
              </button>
              <button type="button" onClick={goHome} className="w-full h-11 rounded-md bg-dark-3 hover:bg-dark-4 text-light-1 text-sm font-medium transition-colors cursor-pointer" data-id="element-3968">
                Înapoi acasă
              </button>
            </div>
          </div>
        </main>
      </div>;
  }
  return <div className="min-h-screen bg-dark-1 flex flex-col" data-id="element-3969">
      <header className="h-16 border-b border-dark-3 flex items-center justify-between px-4 sm:px-6 shrink-0" data-id="element-3970">
        <button type="button" onClick={goHome} className="flex items-center cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-lime-1 rounded-sm" aria-label="Acasă" data-id="element-3971">
          <Logo data-id="element-3972" />
        </button>
        <button type="button" onClick={goHome} className="w-9 h-9 inline-flex items-center justify-center rounded-md text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer" aria-label="Închide" data-id="element-3973">
          <X size={18} weight="bold" data-id="element-3974" />
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8" data-id="element-3975">
        <div className="w-full max-w-lg bg-dark-2 border border-dark-3 rounded-2xl p-6 sm:p-8" data-id="element-3976">
          {/* Logged-in identity strip — visible on all steps before success */}
          {isLoggedInReviewer && step !== 'success' && <div className="flex items-center gap-3 mb-6 -mt-2 px-3 py-2 rounded-xl bg-dark-1 border border-dark-3" data-id="element-3977">
              {session?.avatar ? <img src={session.avatar} alt={session.name} className="w-7 h-7 rounded-full object-cover shrink-0" data-id="element-3978" /> : <div className="w-7 h-7 rounded-full bg-dark-3 inline-flex items-center justify-center shrink-0 text-light-2 text-xs font-medium" data-id="element-3979">
                  {session?.name?.[0]?.toUpperCase() || 'U'}
                </div>}
              <div className="min-w-0 flex-1" data-id="element-3980">
                <p className="text-xs text-light-3 leading-tight" data-id="element-3981">Scrii ca</p>
                <p className="text-sm font-medium text-light-1 truncate flex items-center gap-1.5" data-id="element-3982">
                  {session?.name}
                  <SealCheck size={14} weight="fill" className="text-lime-1 shrink-0" data-id="element-3983" />
                </p>
              </div>
              <button type="button" onClick={handleSwitchAccount} className="text-xs text-light-3 hover:text-light-1 hover:underline underline-offset-4 cursor-pointer transition-colors shrink-0" data-id="element-3984">
                Nu ești tu?
              </button>
            </div>}

          {step === 'ratings' && <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3985">
              <div className="text-center" data-id="element-3986">
                <h1 className="text-xl font-semibold text-light-1 mb-2" data-id="element-3987">
                  {TITLE_BY_SUBJECT[subjectType](subjectName)}
                </h1>
                <p className="text-sm text-light-3" data-id="element-3988">
                  {SUBTITLE_BY_SUBJECT[subjectType]}
                </p>
              </div>

              <div className="space-y-6" data-id="element-3989">
                {categories.map(cat => <div key={cat.key} className="flex items-center justify-between gap-4" data-id="element-3990">
                    <span className="text-sm font-medium text-light-2" data-id="element-3991">
                      {cat.label}
                    </span>
                    <div className="flex items-center gap-1 shrink-0" data-id="element-3992">
                      {[1, 2, 3, 4, 5].map(val => <button key={val} type="button" onClick={() => handleRating(cat.key, val)} className="p-1 hover:scale-110 transition-transform cursor-pointer" aria-label={`${val} stele`} data-id="element-3993">
                          <Star size={24} weight={(ratings[cat.key] || 0) >= val ? 'fill' : 'regular'} className={(ratings[cat.key] || 0) >= val ? 'text-lime-1' : 'text-dark-4'} data-id="element-3994" />
                        </button>)}
                    </div>
                  </div>)}
              </div>

              <button type="button" onClick={() => setStep('details')} disabled={!allRated} className={cn('w-full h-12 rounded-md text-sm font-semibold transition-colors', allRated ? 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer' : 'bg-dark-3 text-light-4 cursor-not-allowed')} data-id="element-3995">
                Continuă
              </button>
            </div>}

          {step === 'details' && <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3996">
              <div data-id="element-3997">
                <h2 className="text-lg font-semibold text-light-1 mb-1" data-id="element-3998">
                  Scrie o recenzie
                </h2>
                <p className="text-sm text-light-3" data-id="element-3999">
                  {DETAILS_HINT_BY_SUBJECT[subjectType]}
                </p>
              </div>

              <div className="space-y-4" data-id="element-4000">
                <div className="space-y-1.5" data-id="element-4001">
                  <label className="text-xs font-medium text-light-2" data-id="element-4002">
                    Recenzia ta
                  </label>
                  <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder={REVIEW_PROMPT_BY_SUBJECT[subjectType](subjectName)} rows={4} data-id="element-4003" />
                  <p className="text-xs text-light-4" data-id="element-4004">Minim 20 de caractere.</p>
                </div>

                {/* Anon flow needs name + email for verification. Logged-in
                 reviewers skip this entirely — identity is taken from
                 session. */}
                {!isLoggedInReviewer && <>
                    <div className="space-y-1.5" data-id="element-4005">
                      <div className="grid grid-cols-2 gap-4" data-id="element-4006">
                        <div className="space-y-1.5" data-id="element-4007">
                          <label className="text-xs font-medium text-light-2" data-id="element-4008">
                            Prenume
                          </label>
                          <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="ex: Radu" data-id="element-4009" />
                        </div>
                        <div className="space-y-1.5" data-id="element-4010">
                          <label className="text-xs font-medium text-light-2" data-id="element-4011">
                            Nume de familie
                          </label>
                          <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="ex: Marinescu" data-id="element-4012" />
                        </div>
                      </div>
                      <p className="text-[11px] text-light-4 leading-relaxed" data-id="element-4013">
                        Pentru validare, avem nevoie de numele întreg așa cum
                        apare pe contractul de închiriere. Pe profilul public,
                        numele de familie va apărea prescurtat (ex:{' '}
                        <span className="text-light-3" data-id="element-4014">Radu M.</span>).
                      </p>
                    </div>

                    <div className="space-y-1.5" data-id="element-4015">
                      <label className="text-xs font-medium text-light-2" data-id="element-4016">
                        Emailul tău
                      </label>
                      <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Pentru verificare" data-id="element-4017" />
                    </div>
                  </>}
              </div>

              {!isLoggedInReviewer && <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-dark-3/60 border border-dark-4" data-id="element-4018">
                  <ShieldCheck size={16} weight="regular" className="text-light-2 shrink-0 mt-0.5" data-id="element-4019" />
                  <p className="text-xs text-light-3 leading-relaxed" data-id="element-4020">
                    Emailul tău nu va fi public. Îl folosim doar pentru a
                    verifica că ești o persoană reală.
                  </p>
                </div>}

              <div className="flex gap-3" data-id="element-4021">
                <button type="button" onClick={() => setStep('ratings')} className="px-4 h-12 rounded-md bg-dark-3 hover:bg-dark-4 text-light-1 text-sm font-medium transition-colors cursor-pointer" data-id="element-4022">
                  Înapoi
                </button>
                <button type="button" onClick={isLoggedInReviewer ? handlePublishLoggedIn : handleSendOtp} disabled={!detailsValid || isSubmitting} className={cn('flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-md text-sm font-semibold transition-colors', detailsValid && !isSubmitting ? 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer' : 'bg-dark-3 text-light-4 cursor-not-allowed')} data-id="element-4023">
                  {isSubmitting ? <>
                      <Spinner size="sm" data-id="element-4024" />
                      {isLoggedInReviewer ? 'Se publică...' : 'Se trimite...'}
                    </> : isLoggedInReviewer ? 'Publică recenzia' : 'Verifică emailul'}
                </button>
              </div>
            </div>}

          {step === 'otp' && <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-4025">
              <div className="flex flex-col items-center text-center" data-id="element-4026">
                <div className="w-14 h-14 rounded-full bg-dark-3 border border-dark-4 flex items-center justify-center mb-4" data-id="element-4027">
                  <EnvelopeSimple size={26} weight="regular" className="text-light-2" data-id="element-4028" />
                </div>
                <h2 className="text-lg font-semibold text-light-1 mb-1" data-id="element-4029">
                  Confirmă adresa de email
                </h2>
                <p className="text-sm text-light-3" data-id="element-4030">
                  Am trimis un cod de 6 cifre la <strong data-id="element-4031">{email}</strong>
                </p>
              </div>

              <div className="space-y-1.5" data-id="element-4032">
                <label className="text-xs font-medium text-light-2 text-center block" data-id="element-4033">
                  Codul de verificare
                </label>
                <Input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" className="text-center text-xl tracking-widest font-semibold h-14" data-id="element-4034" />
              </div>

              <div className="flex gap-3" data-id="element-4035">
                <button type="button" onClick={() => setStep('details')} className="px-4 h-12 rounded-md bg-dark-3 hover:bg-dark-4 text-light-1 text-sm font-medium transition-colors cursor-pointer" data-id="element-4036">
                  Înapoi
                </button>
                <button type="button" onClick={handleVerifyOtp} disabled={otp.length !== 6 || isSubmitting} className={cn('flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-md text-sm font-semibold transition-colors', otp.length === 6 && !isSubmitting ? 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer' : 'bg-dark-3 text-light-4 cursor-not-allowed')} data-id="element-4037">
                  {isSubmitting ? <>
                      <Spinner size="sm" data-id="element-4038" />
                      Se trimite...
                    </> :
              // Anonymous reviewers reach this step → review goes into
              // pending state until document validation, so CTA reads
              // "trimite" not "publică".
              'Trimite recenzia'}
                </button>
              </div>
            </div>}

          {step === 'success' && (() => {
          // Send the reviewer back to the subject's profile so they can
          // see their published review in context — industry standard
          // (Google Reviews, Airbnb, Booking). Fall back to the landing
          // page only when we don't know the subject slug (e.g. broken
          // invite link).
          const subjectRoute = subjectType === 'tenant' ? PAGE_ROUTE.TENANT_PROFILE : PAGE_ROUTE.LANDLORD_PROFILE;
          const canReturnToSubject = !!subjectSlug;
          const goToSubject = () => subjectSlug ? navigate(subjectRoute, {
            slug: subjectSlug
          }) : goHome();
          return <div className="flex flex-col items-center text-center py-8 animate-in zoom-in-95 duration-500" data-id="element-4039">
                  <div className="w-20 h-20 rounded-full bg-lime-5 border border-lime-4 flex items-center justify-center mb-6" data-id="element-4040">
                    <Check size={40} weight="bold" className="text-lime-1" data-id="element-4041" />
                  </div>
                  <h2 className="text-2xl font-bold text-light-1 mb-2" data-id="element-4042">
                    Mulțumim!
                  </h2>
                  <p className="text-sm text-light-3 mb-6 max-w-sm" data-id="element-4043">
                    {isLoggedInReviewer ? <>
                        Recenzia ta a fost publicată cu succes pe profilul lui{' '}
                        {subjectName}. Acest lucru îl va ajuta enorm.
                      </> : <>
                        Recenzia ta a fost trimisă. Va fi publicată pe profilul
                        lui {subjectName} după ce o validează cu un document
                        oficial de închiriere (contract, C168 ANAF, etc.).
                      </>}
                  </p>

                  {/* Upsell nudge — only for anonymous off-platform reviewers.
                            Goal: convert reviewers (likely landlords without an
                            account) into Izio users so they can collect reviews
                            themselves. */}
                  {!isLoggedInReviewer && <div className="w-full max-w-sm mb-6 px-4 py-3 rounded-xl bg-dark-1 border border-dark-3 text-left" data-id="element-4044">
                      <p className="text-xs font-semibold text-light-1 mb-1" data-id="element-4045">
                        Vrei și tu recenzii de la chiriași?
                      </p>
                      <p className="text-xs text-light-3 leading-relaxed mb-3" data-id="element-4046">
                        Proprietarii cu profil pe Izio închiriază de 4x mai
                        repede și au mai puține probleme cu chiriașii.
                      </p>
                      <button type="button" onClick={() => navigate(PAGE_ROUTE.REGISTER)} className="text-xs font-medium text-lime-1 hover:text-lime-2 hover:underline underline-offset-4 cursor-pointer transition-colors" data-id="element-4047">
                        Creează un cont gratuit →
                      </button>
                    </div>}

                  <div className="flex flex-col gap-2 w-full max-w-xs" data-id="element-4048">
                    <button type="button" onClick={canReturnToSubject ? goToSubject : goHome} className="h-12 px-6 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 text-sm font-semibold transition-colors cursor-pointer" data-id="element-4049">
                      {canReturnToSubject ? `Vezi profilul lui ${subjectName}` : 'Înapoi acasă'}
                    </button>
                    {canReturnToSubject && <button type="button" onClick={goHome} className="h-11 px-6 rounded-md text-light-3 hover:text-light-1 text-sm font-medium transition-colors cursor-pointer" data-id="element-4050">
                        Înapoi acasă
                      </button>}
                  </div>
                </div>;
        })()}
        </div>
      </main>
    </div>;
}